'use client'

import { motion } from 'framer-motion'
import { Check, Layers, ArrowRight } from 'lucide-react'

interface Tier {
  name: string
  tagline: string
  turnsOn: string[]
  carriesOver?: string
  highlight?: boolean
}

interface AutomotiveTiersSectionProps {
  eyebrow?: string
  heading: string
  intro?: string
  tiers: Tier[]
  note?: string
  ctaLabel?: string
  ctaHref?: string
}

export function AutomotiveTiersSection({
  eyebrow,
  heading,
  intro,
  tiers,
  note,
  ctaLabel,
  ctaHref,
}: AutomotiveTiersSectionProps) {
  const isExternal = ctaHref ? /^https?:\/\//.test(ctaHref) : false
  return (
    <section className="relative py-24 overflow-hidden bg-muted/20">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          {eyebrow && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="reveal reveal-delay-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2F5FE8]/10 border border-[#2F5FE8]/20 mb-6"
            >
              <Layers className="h-4 w-4 text-[#2F5FE8]" />
              <span className="eyebrow">{eyebrow}</span>
            </motion.div>
          )}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
          >
            {heading}
          </motion.h2>
          {intro && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="reveal reveal-delay-2 text-lg text-muted-foreground"
            >
              {intro}
            </motion.p>
          )}
        </div>

        {/* Tier cards */}
        <div className="grid gap-6 lg:grid-cols-3 max-w-6xl mx-auto items-start">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className={`reveal reveal-delay-3 lift-hover relative flex flex-col rounded-3xl border p-7 bg-white dark:bg-zinc-900 ${
                tier.highlight
                  ? 'border-[#2F5FE8]/40 shadow-[0_20px_50px_rgba(47,95,232,0.18)] lg:-mt-3 lg:mb-3'
                  : 'border-border/50 shadow-[0_4px_30px_rgba(0,0,0,0.06)]'
              }`}
            >
              {tier.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#2F5FE8] px-4 py-1 text-[11px] font-bold uppercase tracking-widest text-white">
                  Most popular
                </span>
              )}
              <h3 className="reveal reveal-delay-0 text-xl font-bold text-foreground">{tier.name}</h3>
              <p className="reveal reveal-delay-1 mt-1 text-sm font-medium text-[#2F5FE8]/90">{tier.tagline}</p>

              {tier.carriesOver && (
                <p className="reveal reveal-delay-2 mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {tier.carriesOver}
                </p>
              )}

              <ul className="mt-4 space-y-2.5 flex-1">
                {tier.turnsOn.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-[#2F5FE8] flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Note + CTA */}
        <div className="max-w-3xl mx-auto mt-12 text-center">
          {note && <p className="text-sm text-muted-foreground mb-6">{note}</p>}
          {ctaLabel && ctaHref && (
            isExternal ? (
              <a
                href={ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#2F5FE8] px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#2F5FE8]/20 hover:bg-[#2049BE] transition-colors"
              >
                {ctaLabel}
                <ArrowRight className="h-4 w-4" />
              </a>
            ) : (
              <a
                href={ctaHref}
                className="inline-flex items-center gap-2 rounded-full bg-[#2F5FE8] px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#2F5FE8]/20 hover:bg-[#2049BE] transition-colors"
              >
                {ctaLabel}
                <ArrowRight className="h-4 w-4" />
              </a>
            )
          )}
        </div>
      </div>
    </section>
  )
}
